import {
    Color,
    Mesh,
    MeshStandardMaterial,
    MirroredRepeatWrapping,
    PlaneGeometry,
    RepeatWrapping,
    Scene,
} from "three";
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler.js";
import { RoomElementType } from "./enums";
import { GraphicService } from "./GraphicService";
import { IRoomSize, ISceneConfiguration } from "./interfaces";
import { LoaderService } from "./LoaderService";
import { Room } from "./Room";
import { AccessPointDragHandler } from "./AccessPointDragHandler";
export class BuilderService {
    _floor: Mesh | undefined;
    _floorGlow: Mesh | undefined;
    _floorSampler: MeshSurfaceSampler | undefined;
    _Ceiling: Mesh | undefined;
    _Logo: Mesh | undefined;
    _CMRLogo: Mesh | undefined;
    _scene!: Scene;
    _walls: Mesh[] = [];
    _roomSize!: IRoomSize;
    private _loader!: LoaderService;
    private _wallColor!: Color;
    private _wallNorthColor!: Color;
    private _floorColor!: Color;
    private M_Floor!: MeshStandardMaterial;
    private M_FloorGlow!: MeshStandardMaterial;
    private M_Logo!: MeshStandardMaterial;
    private M_Ceilling!: MeshStandardMaterial;
    private M_Wall1!: MeshStandardMaterial;
    private M_Wall2!: MeshStandardMaterial;
    private M_WallNorth!: MeshStandardMaterial;
    room!: Room;

    private _accessPointDragHandler: AccessPointDragHandler | undefined;

    /**
     * Build the floor of the room
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    setup(
        sceneConf: ISceneConfiguration,
        loader: LoaderService,
        scene: Scene = GraphicService.scene
    ) {
        this._roomSize = sceneConf.roomSize;
        this._scene = scene;
        this._loader = loader;

        this.room = new Room(JSON.parse(JSON.stringify(sceneConf)));

        let hexColor = "#ffffff";
        if (this._loader._modelData.room.wall.color && !this._loader._modelData.room.wall.map) {
            hexColor = this._loader._modelData.room.wall.color;
        }
        this._wallColor = new Color(hexColor);
        hexColor = "#ffffff";
        if (
            this._loader._modelData.room.wallNorth.color
            // !this._loader._modelData.room.wallNorth.map
        ) {
            hexColor = this._loader._modelData.room.wallNorth.color;
        }
        this._wallNorthColor = new Color(hexColor);
        hexColor = "#ffffff";
        if (this._loader._modelData.room.floor.color) {
            hexColor = this._loader._modelData.room.floor.color;
        }
        this._floorColor = new Color(hexColor);
        hexColor = "#444444";
        if (!this.M_Wall1) {
            this.M_Wall1 = new MeshStandardMaterial({ roughness: 0.6 });
        }
        if (!this.M_Wall2) {
            this.M_Wall2 = new MeshStandardMaterial({ roughness: 0.6 });
        }
        if (!this.M_WallNorth) {
            this.M_WallNorth = new MeshStandardMaterial({
                roughness: 0.6,
                color: this._wallNorthColor,
            });
        }
        if (!this.M_Floor) {
            this.M_Floor = new MeshStandardMaterial({ color: this._floorColor, roughness: 0.8 });
        }
        if (!this.M_FloorGlow) {
            this.M_FloorGlow = new MeshStandardMaterial({
                color: this._floorColor,
                roughness: 0.5,
            });
        }
        if (!this.M_Logo)
            this.M_Logo = new MeshStandardMaterial({ roughness: 0.3, transparent: true });
        if (!this.M_Ceilling) this.M_Ceilling = new MeshStandardMaterial({ roughness: 0.7 });

        this.buildAll();

        const canvas = GraphicService.renderer.domElement;
        this._accessPointDragHandler = new AccessPointDragHandler(this.room, canvas);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    buildAll() {
        this.buildFloor();
        this.buildWalls();
        // this.buildCeiling();
        if (this._loader.assetsloaded) {
            this.room.buildScene();
        }
    }

    clearAll(): void {
        this.room.clearAll();
        if (this._floor) this._scene.remove(this._floor);
        if (this._Ceiling) this._scene.remove(this._Ceiling);
        if (this._Logo) this._scene.remove(this._Logo);
        for (const wall of this._walls) {
            this._scene.remove(wall);
        }
        this._walls = [];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    buildFloor() {
        const geom = new PlaneGeometry(this._roomSize.width, this._roomSize.length);
        const mat = this.M_Floor;
        const logoSize = this._loader._modelData.room?.logo.size;
        const geomLogo = new PlaneGeometry(logoSize.width, logoSize.length);
        const m_logo = this.M_Logo;
        if (!this.M_Logo.map) {
            const logoURL = [
                this._loader._modelData.Paths.MapsFolder,
                this._loader._modelData.room.logo.url,
            ].join("/");
            this._loader.loadTexture(logoURL, (tex) => {
                m_logo.map = tex;
                m_logo.needsUpdate = true;
            });
        }
        const logo = new Mesh(geomLogo, m_logo);
        this._Logo = logo;
        const z = Math.min(this._roomSize.length / 4.0, 2.0);
        logo.position.set(0.0, 0.01, z);
        logo.rotation.x = -Math.PI / 2;
        logo.receiveShadow = true;
        this._scene.add(logo);
        if (!this.M_FloorGlow.map) {
            if (this._loader._modelData.room.floor.glowMap) {
                const url = [
                    this._loader._modelData.Paths.MapsFolder,
                    this._loader._modelData.room.floor.glowMap,
                ].join("/");
                this.M_FloorGlow.transparent = true;
                this._loader.loadTexture(url, (tex) => {
                    this.M_FloorGlow.map = tex;
                    tex.anisotropy = 8;
                    this.M_FloorGlow.needsUpdate = true;
                });
            }
        }
        this._floorGlow = new Mesh(geom, this.M_FloorGlow);
        this._floorGlow.rotation.x = -Math.PI / 2;
        this._scene.add(this._floorGlow);
        this._loader.loadTexture(
            `${this._loader._modelData.Paths.MapsFolder}/${this._loader._modelData.room.floor.oaMap}`,
            (texture) => {
                mat.aoMap = texture;
                mat.aoMap.repeat.x = this._roomSize.width / 2;
                mat.aoMap.repeat.y = this._roomSize.length / 2;
                mat.aoMap.wrapS = MirroredRepeatWrapping;
                mat.aoMap.wrapT = MirroredRepeatWrapping;
                mat.needsUpdate = true;
            }
        );
        this._loader.loadTexture(
            `${this._loader._modelData.Paths.MapsFolder}/${this._loader._modelData.room.floor.normalMap}`,
            (texture) => {
                mat.normalMap = texture;
                mat.normalMap.repeat.x = this._roomSize.width / 2;
                mat.normalMap.repeat.y = this._roomSize.length / 2;
                mat.normalMap.wrapS = MirroredRepeatWrapping;
                mat.normalMap.wrapT = MirroredRepeatWrapping;
                mat.needsUpdate = true;
            }
        );

        this._floor = new Mesh(geom, mat);
        this._floor.layers.set(0);
        this._floor.name = "floor";
        this._floor.receiveShadow = true;
        this._floor.userData["type"] = RoomElementType.Floor;
        this._floor.rotation.x = -Math.PI / 2;
        this._scene.add(this._floor);

        // Set surfaceSampler
        this._floorSampler = new MeshSurfaceSampler(this._floor).build();
    }

    buildWalls(): void {
        const geom1 = new PlaneGeometry(this._roomSize.length, this._roomSize.height);
        const geom2 = new PlaneGeometry(this._roomSize.width, this._roomSize.height);
        const cmrSize = this._loader._modelData.room?.cmrlogo?.size;
        const cmrLogoGeom = new PlaneGeometry(cmrSize.width, cmrSize.length);
        const M_CMRLogo = new MeshStandardMaterial();
        M_CMRLogo.transparent = true;
        if (!M_CMRLogo.map) {
            if (this._loader._modelData.room.cmrlogo.url) {
                const cmrPath = [
                    this._loader._modelData.Paths.MapsFolder,
                    this._loader._modelData.room.cmrlogo.url,
                ].join("/");
                this._loader.loadTexture(cmrPath, (tex) => {
                    M_CMRLogo.map = tex;
                    tex.anisotropy = 8;
                    M_CMRLogo.needsUpdate = true;
                });
            }
        }
        this._CMRLogo = new Mesh(cmrLogoGeom, M_CMRLogo);

        this._scene.add(this._CMRLogo);
        if (this._loader._modelData.room.wall.map && !this.M_Wall1.map) {
            const diffuseWallURL = [
                this._loader._modelData.Paths.MapsFolder,
                this._loader._modelData.room.wall.map,
            ].join("/");
            this._loader.loadTexture(diffuseWallURL, (texture) => {
                this.M_Wall1.map = texture;
                this.M_Wall1.needsUpdate = true;
                this.M_Wall2.map = texture;
                this.M_Wall2.needsUpdate = true;
            });
        }
        // if (this._loader._modelData.room.wallNorth.map && !this.M_WallNorth.map) {
        //     const diffuseWallNorthURL = [
        //         this._loader._modelData.Paths.MapsFolder,
        //         this._loader._modelData.room.wallNorth.map,
        //     ].join("/");
        //     this._loader.loadTexture(diffuseWallNorthURL, (texture) => {
        //         this.M_WallNorth.map = texture;
        //         this.M_WallNorth.needsUpdate = true;
        //     });
        // }
        /* if (!this.M_Wall1.normalMap) {
            const normalURL = [
                this._loader._modelData.Paths.MapsFolder,
                this._loader._modelData.room.wall.normalMap,
            ].join("/");
            this._loader.loadTexture(normalURL, (texture) => {
                this.M_Wall1.normalMap = texture;
                this.M_Wall1.normalMap.repeat.set(
                    this._roomSize.width * 4,
                    this._roomSize.height * 4
                );
                this.M_Wall1.normalMap.wrapT = this.M_Wall1.normalMap.wrapS = RepeatWrapping;
                this.M_Wall1.needsUpdate = true;
            });
        }

        if (!this.M_Wall2.normalMap) {
            const normalURL = [
                this._loader._modelData.Paths.MapsFolder,
                this._loader._modelData.room.wall.normalMap,
            ].join("/");
            this._loader.loadTexture(normalURL, (texture) => {
                this.M_Wall2.normalMap = texture;
                this.M_Wall2.normalMap.repeat.set(
                    this._roomSize.length * 4,
                    this._roomSize.height * 4
                );
                this.M_Wall2.normalMap.wrapT = this.M_Wall2.normalMap.wrapS = RepeatWrapping;
                this.M_Wall2.needsUpdate = true;
            });
        } */

        const wall1 = new Mesh(geom1, this.M_Wall2);
        this._walls.push(wall1);
        const wall2 = new Mesh(geom2, this.M_WallNorth);
        this._walls.push(wall2);
        const wall3 = new Mesh(geom1, this.M_Wall2);
        this._walls.push(wall3);
        const wall4 = new Mesh(geom2, this.M_Wall1);
        this._walls.push(wall4);

        wall1.rotation.y = Math.PI / 2.0;
        wall1.position.set(-this._roomSize.width / 2.0, this._roomSize.height / 2.0, 0.0);
        wall1.receiveShadow = true;
        wall1.name = "wall1";

        wall2.position.set(0.0, this._roomSize.height / 2.0, -this._roomSize.length / 2.0);
        this._CMRLogo.position.set(
            0.0,
            this._roomSize.height / 2.0,
            -this._roomSize.length / 2.0 + 0.0001
        );
        wall2.receiveShadow = true;
        wall2.name = "wall2";

        wall3.rotation.y = -Math.PI / 2.0;
        wall3.position.set(this._roomSize.width / 2.0, this._roomSize.height / 2.0, 0.0);
        wall3.receiveShadow = true;
        wall3.name = "wall3";

        wall4.rotation.y = Math.PI;
        wall4.position.set(0.0, this._roomSize.height / 2.0, this._roomSize.length / 2.0);
        wall4.receiveShadow = true;
        wall4.name = "wall4";

        this._scene.add(wall1);
        this._scene.add(wall2);
        this._scene.add(wall3);
        this._scene.add(wall4);
    }
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    buildCeiling() {
        const geom = new PlaneGeometry(this._roomSize.width, this._roomSize.length);
        const mat = this.M_Ceilling;
        const ceilling = new Mesh(geom, mat);

        if (!mat.normalMap) {
            const normalURL = [
                this._loader._modelData.Paths.MapsFolder,
                this._loader._modelData.room.ceiling.normalMap,
            ].join("/");
            this._loader.loadTexture(normalURL, (tex) => {
                mat.normalMap = tex;
                tex.anisotropy = 8;
                mat.normalMap.repeat.x = this._roomSize.width * 4;
                mat.normalMap.repeat.y = this._roomSize.length * 4;
                mat.normalMap.wrapS = RepeatWrapping;
                mat.normalMap.wrapT = RepeatWrapping;
                mat.needsUpdate = true;
            }); /**/
        }

        this._Ceiling = ceilling;
        ceilling.name = "ceiling";
        ceilling.position.y = this._roomSize.height;
        ceilling.rotation.x = Math.PI / 2;
        this._scene.add(ceilling);
    }
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    updateRoomSize(newSize: IRoomSize) {
        this._roomSize = newSize;
        this.room.currentConfiguration.roomSize = newSize;
        // update Walls
        const geom1 = new PlaneGeometry(this._roomSize.length, this._roomSize.height);
        const geom2 = new PlaneGeometry(this._roomSize.width, this._roomSize.height);
        if (this.M_Wall1.normalMap) {
            this.M_Wall1.normalMap.repeat.set(this._roomSize.width * 4, this._roomSize.height * 4);
            this.M_Wall1.needsUpdate = true;
        }
        if (this.M_Wall2.normalMap) {
            this.M_Wall2.normalMap.repeat.set(this._roomSize.length * 4, this._roomSize.height * 4);
            this.M_Wall2.needsUpdate = true;
        }
        this._walls[0].geometry = geom1;
        this._walls[0].position.set(-this._roomSize.width / 2.0, this._roomSize.height / 2.0, 0.0);

        this._walls[2].geometry = geom1;
        this._walls[2].position.set(this._roomSize.width / 2.0, this._roomSize.height / 2.0, 0.0);

        this._walls[1].geometry = geom2;
        this._walls[1].position.set(0.0, this._roomSize.height / 2.0, -this._roomSize.length / 2.0);

        this._walls[3].geometry = geom2;
        this._walls[3].position.set(0.0, this._roomSize.height / 2.0, this._roomSize.length / 2.0);
        //update Floor
        if (this._floor) {
            const geom = new PlaneGeometry(this._roomSize.width, this._roomSize.length);
            this._floor.geometry = geom;
            if (this.M_Floor.map) {
                this.M_Floor.map.repeat.y = this._roomSize.length / 4;
                this.M_Floor.map.repeat.x = this._roomSize.width / 4;
                this.M_Floor.needsUpdate = true;
            }
            if (this._floorGlow) {
                this._floorGlow.geometry = geom;
            }
        }
        //update Logo
        if (this._Logo) {
            const z = Math.min(this._roomSize.length / 4.0, 2.0);
            this._Logo.position.set(0.0, 0.01, z);
        }
        if (this._CMRLogo) {
            this._CMRLogo.position.set(
                0.0,
                this._roomSize.height / 2.0,
                -this._roomSize.length / 2.0 + 0.0001
            );
        }
        //update Ceiling
        if (this._Ceiling) {
            const CeilingGeom = new PlaneGeometry(this._roomSize.width, this._roomSize.length);
            this._Ceiling.geometry = CeilingGeom;
            this._Ceiling.position.y = this._roomSize.height;
            if (this.M_Ceilling.normalMap) {
                this.M_Ceilling.normalMap.repeat.x = this._roomSize.width * 4;
                this.M_Ceilling.normalMap.repeat.y = this._roomSize.length * 4;
            }
        }

        GraphicService.updateLighting(newSize);

        this.room.refreshAccessPoints();
    }
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    reset(sceneConf: ISceneConfiguration) {
        this.updateRoomSize(sceneConf.roomSize);
        this.room.reset(sceneConf);
    }

    addAccessPoint(wall: "north" | "south" | "east" | "west" = "north", offset = 0): void {
        const scene = JSON.parse(JSON.stringify(this.room.currentConfiguration));

        const id = crypto.getRandomValues(new Uint32Array(2)).join("-");

        if (!scene.accessPoints) scene.accessPoints = [];

        scene.accessPoints.push({
            id,
            wall,
            offset,
        });

        this.room.currentConfiguration = scene;
        this.room.buildAccessPoints();
    }
}
